import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ROLES, getRoleInfo } from '../utils/rolePermissions';
import {
  hasLimitedBackofficeNavigation,
  isMembersEnabled,
  isLemon8Enabled,
  isXhsAiImageEnabled
} from '../utils/featureFlags';

const RoleContext = createContext();

const LIMITED_BACKOFFICE_PERMISSIONS = new Set([
  'dashboard',
  'platform-content',
  'lucky-draw',
  'instagram-images',
  'users'
]);

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

export const RoleProvider = ({ children }) => {
  const [userRole, setUserRole] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]); // Permissions from database
  const [isLoading, setIsLoading] = useState(true);

  const loadUserRole = useCallback(() => {
    try {
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);

        // Map user data to role
        const mappedRole = mapRoleToSystem(userData);
        setUserRole(mappedRole);

        // Use permissions from database if available, otherwise fallback to hardcoded
        if (userData.permissions && Array.isArray(userData.permissions)) {
          setUserPermissions(userData.permissions);
        } else {
          // Fallback to hardcoded permissions
          const roleInfo = getRoleInfo(mappedRole);
          setUserPermissions(roleInfo ? roleInfo.permissions : []);
        }
      } else {
        setUserRole(null);
        setUserPermissions([]);
      }
    } catch (error) {
      console.error('Error loading user role:', error);
      setUserRole(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Get user role from localStorage on mount
    loadUserRole();
  }, [loadUserRole]);

  // Listen for custom events to refresh role data when user logs in
  useEffect(() => {
    const handleUserDataUpdate = () => {
      loadUserRole();
    };

    const handleUserLogout = () => {
      setUserRole(null);
      setUserPermissions([]);
      setIsLoading(false);
    };

    window.addEventListener('userDataUpdated', handleUserDataUpdate);
    window.addEventListener('userLoggedOut', handleUserLogout);

    return () => {
      window.removeEventListener('userDataUpdated', handleUserDataUpdate);
      window.removeEventListener('userLoggedOut', handleUserLogout);
    };
  }, [loadUserRole]);

  // Map role information from database to our role system
  const mapRoleToSystem = (userData) => {
    if (!userData) return null;

    // If we have role_name from the database, use it directly
    if (userData.role_name) {
      const roleName = userData.role_name.toLowerCase();
      if (roleName === 'master_admin') return ROLES.MASTER_ADMIN;
      if (roleName === 'system_admin') return ROLES.SYSTEM_ADMIN;
      if (roleName === 'senior_admin') return ROLES.SENIOR_ADMIN;
      if (roleName === 'admin') return ROLES.ADMIN;
    }

    // Fallback: check role_id
    if (userData.role_id) {
      switch (userData.role_id) {
        case 1: return ROLES.ADMIN;        // admin role
        case 2: return ROLES.SENIOR_ADMIN; // senior_admin role
        case 3: return ROLES.SYSTEM_ADMIN; // system_admin role
        case 4: return ROLES.MASTER_ADMIN; // master_admin role
        default: return ROLES.ADMIN;
      }
    }

    // Legacy fallback: check position field
    if (userData.position) {
      const lowerPosition = userData.position.toLowerCase();
      if (lowerPosition.includes('master') || lowerPosition === 'master_admin') {
        return ROLES.MASTER_ADMIN;
      } else if (lowerPosition.includes('system') || lowerPosition === 'system_admin') {
        return ROLES.SYSTEM_ADMIN;
      } else if (lowerPosition.includes('senior') || lowerPosition === 'senior_admin') {
        return ROLES.SENIOR_ADMIN;
      } else if (lowerPosition.includes('admin') || lowerPosition === 'admin') {
        return ROLES.ADMIN;
      }
    }

    // Default fallback
    return ROLES.ADMIN;
  };

  const updateUserRole = (newRole) => {
    setUserRole(newRole);

    // Update localStorage
    try {
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        userData.role = newRole;
        // Update role information based on the new role
        switch (newRole) {
          case ROLES.ADMIN:
            userData.role_id = 1;
            userData.role_name = 'admin';
            break;
          case ROLES.SENIOR_ADMIN:
            userData.role_id = 2;
            userData.role_name = 'senior_admin';
            break;
          case ROLES.SYSTEM_ADMIN:
            userData.role_id = 3;
            userData.role_name = 'system_admin';
            break;
          case ROLES.MASTER_ADMIN:
            userData.role_id = 4;
            userData.role_name = 'master_admin';
            break;
          default:
            // Keep existing role data if role is not recognized
            break;
        }
        localStorage.setItem('userData', JSON.stringify(userData));
      }
    } catch (error) {
      console.error('Error updating user role in localStorage:', error);
    }
  };

  const checkPermission = (permission) => {
    if (hasLimitedBackofficeNavigation() && !LIMITED_BACKOFFICE_PERMISSIONS.has(permission)) {
      return false;
    }

    return userPermissions.includes(permission);
  };

  const getAvailableRoutes = () => {
    // Import NAVIGATION_ITEMS locally to avoid circular dependency
    const NAVIGATION_ITEMS = [
      { id: 'master-dashboard', label: 'Master Dashboard', icon: 'Apps', path: '/master-dashboard', requiredPermission: 'master-dashboard' },
      { id: 'dashboard', label: 'Dashboard', icon: 'Dashboard', path: '/dashboard', requiredPermission: 'dashboard' },
      { id: 'platform-content', label: 'Platform Content', icon: 'Article', path: '/platform-content', requiredPermission: 'platform-content' },
      ...(isMembersEnabled() ? [{ id: 'page-visuals', label: 'Page Visuals', icon: 'Collections', path: '/page-visuals', requiredPermission: 'platform-content' }] : []),
      { id: 'banner-images', label: 'Banner Image', icon: 'Collections', path: '/banner-images', requiredPermission: 'banner-images' },
      { id: 'lucky-draw', label: 'Lucky Draw', icon: 'Redeem', path: '/lucky-draw', requiredPermission: 'lucky-draw' },
      ...(isMembersEnabled()
        ? [{ id: 'members', label: 'Members', icon: 'People', path: '/members', requiredPermission: 'lucky-draw' }]
        : []),
      { id: 'instagram-images', label: 'Instagram Images', icon: 'PhotoCamera', path: '/instagram-images', requiredPermission: 'instagram-images' },
      { id: 'xhs-ai-image', label: 'AI Image Generator', icon: 'Image', path: '/xhs-ai-image', requiredPermission: 'xhs-ai-image' },
      { id: 'xhs', label: 'XHS Content Generator', icon: 'AutoStories', path: '/xhs', requiredPermission: 'xhs-content-generator' },
      { id: 'xhs-copy', label: 'XHS Content Generator', icon: 'AutoAwesome', path: '/xhs-copy', requiredPermission: 'xhs-content-generator-copy' },
      { id: 'google-content-generator', label: 'Google Content Generator', icon: 'LocationOn', path: '/google-content-generator', requiredPermission: 'google-content-generator' },
      { id: 'facebook-content-generator', label: 'Facebook Content Generator', icon: 'ThumbUp', path: '/facebook-content-generator', requiredPermission: 'facebook-content-generator' },
      ...(isLemon8Enabled()
        ? [{ id: 'lemon8-content-generator', label: 'Lemon8 Content Generator', icon: 'Article', path: '/lemon8-content-generator', requiredPermission: 'lemon8-content-generator' }]
        : []),
      { id: 'users', label: 'Users', icon: 'People', path: '/users', requiredPermission: 'users' },
      { id: 'profile', label: 'Profile', icon: 'Person', path: '/profile', requiredPermission: 'profile' }
    ];

    return NAVIGATION_ITEMS.filter(item => {
      if (item.id === 'xhs-ai-image' && !isXhsAiImageEnabled()) {
        return false;
      }
      if (Array.isArray(item.requiredPermission)) {
        return item.requiredPermission.some(perm => checkPermission(perm));
      }
      return checkPermission(item.requiredPermission);
    })
  };

  const canAccessRoute = (routePath) => {
    if (routePath === '/xhs-ai-image' && !isXhsAiImageEnabled()) {
      return false;
    }
    const NAVIGATION_ITEMS = [
      { id: 'master-dashboard', label: 'Master Dashboard', icon: 'Apps', path: '/master-dashboard', requiredPermission: 'master-dashboard' },
      { id: 'dashboard', label: 'Dashboard', icon: 'Dashboard', path: '/dashboard', requiredPermission: 'dashboard' },
      { id: 'xhs', label: 'XHS Content Generator', icon: 'AutoStories', path: '/xhs', requiredPermission: 'xhs-content-generator' },
      { id: 'xhs-copy', label: 'XHS Content Generator', icon: 'AutoAwesome', path: '/xhs-copy', requiredPermission: 'xhs-content-generator-copy' },
      { id: 'xhs-images', label: 'XHS Images', icon: 'Image', path: '/xhs-images', requiredPermission: 'xhs-images' },
      { id: 'platform-content', label: 'Platform Content', icon: 'Article', path: '/platform-content', requiredPermission: 'platform-content' },
      ...(isMembersEnabled() ? [{ id: 'page-visuals', label: 'Page Visuals', icon: 'Collections', path: '/page-visuals', requiredPermission: 'platform-content' }] : []),
      { id: 'banner-images', label: 'Banner Image', icon: 'Collections', path: '/banner-images', requiredPermission: 'banner-images' },
      { id: 'lucky-draw', label: 'Lucky Draw', icon: 'Redeem', path: '/lucky-draw', requiredPermission: 'lucky-draw' },
      ...(isMembersEnabled()
        ? [{ id: 'members', label: 'Members', icon: 'People', path: '/members', requiredPermission: 'lucky-draw' }]
        : []),
      { id: 'instagram-images', label: 'Instagram Images', icon: 'PhotoCamera', path: '/instagram-images', requiredPermission: 'instagram-images' },
      { id: 'xhs-ai-image', label: 'AI Image Generator', icon: 'Image', path: '/xhs-ai-image', requiredPermission: 'xhs-ai-image' },
      { id: 'xhs-content-generator', label: 'XHS Content Generator', icon: 'AutoStories', path: '/xhs-content-generator', requiredPermission: 'xhs-content-generator' },
      { id: 'google-content-generator', label: 'Google Content Generator', icon: 'LocationOn', path: '/google-content-generator', requiredPermission: 'google-content-generator' },
      { id: 'facebook-content-generator', label: 'Facebook Content Generator', icon: 'ThumbUp', path: '/facebook-content-generator', requiredPermission: 'facebook-content-generator' },
      ...(isLemon8Enabled()
        ? [{ id: 'lemon8-content-generator', label: 'Lemon8 Content Generator', icon: 'Article', path: '/lemon8-content-generator', requiredPermission: 'lemon8-content-generator' }]
        : []),
      { id: 'users', label: 'Users', icon: 'People', path: '/users', requiredPermission: 'users' },
      { id: 'profile', label: 'Profile', icon: 'Person', path: '/profile', requiredPermission: 'profile' }
    ];

    const navigationItem = NAVIGATION_ITEMS.find(item => item.path === routePath || (routePath.startsWith('/xhs') && item.path.startsWith('/xhs')));
    if (!navigationItem) {
      return false; // Unknown route
    }

    // Handle array permissions
    if (Array.isArray(navigationItem.requiredPermission)) {
      return navigationItem.requiredPermission.some(perm => checkPermission(perm));
    }
    return checkPermission(navigationItem.requiredPermission);
  };

  const getRoleDisplayInfo = () => {
    return getRoleInfo(userRole);
  };

  const clearRole = () => {
    setUserRole(null);
    setUserPermissions([]);
  };

  const refreshRole = () => {
    setIsLoading(true);
    try {
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);

        // Map user data to role
        const mappedRole = mapRoleToSystem(userData);
        setUserRole(mappedRole);

        // Use permissions from database if available, otherwise fallback to hardcoded
        if (userData.permissions && Array.isArray(userData.permissions)) {
          setUserPermissions(userData.permissions);
        } else {
          // Fallback to hardcoded permissions
          const roleInfo = getRoleInfo(mappedRole);
          setUserPermissions(roleInfo ? roleInfo.permissions : []);
        }
      } else {
        setUserRole(null);
        setUserPermissions([]);
      }
    } catch (error) {
      console.error('Error refreshing user role:', error);
      setUserRole(null);
      setUserPermissions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    userRole,
    userPermissions,
    isLoading,
    updateUserRole,
    checkPermission,
    getAvailableRoutes,
    canAccessRoute,
    getRoleDisplayInfo,
    clearRole,
    refreshRole,
    // Convenience methods
    isAdmin: userRole === ROLES.ADMIN,
    isSeniorAdmin: userRole === ROLES.SENIOR_ADMIN,
    isSystemAdmin: userRole === ROLES.SYSTEM_ADMIN,
    isMasterAdmin: userRole === ROLES.MASTER_ADMIN,
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
};
